/**
 * Fases do site principal (ORD-F-054 item 2; §8 de 17/10 ~19h30): a página inicial até 24/10 23h59:59 (do FRONT-2,
 * pedaço `home`), a pré-apuração de 25/10 00h00 às 16h59:59 (pedaço `preOpen`), a apuração a partir das 17h00 (o AppShell,
 * já no JS crítico) e, depois dela, a abertura pós-apuração (`after`, pedido de 26/10: a página inicial com os serviços e o
 * resultado; a apuração completa a um toque). A fase sai do relógio da fonte (o `now` dos efeitos, corrigido pelo
 * servidor); antes de o relógio ligar, do relógio do aparelho, só para o 1º quadro. As trocas acontecem sem recarregar: o
 * `App` lê `sitePhase`, um sinal calculado que só muda nas fronteiras (nenhum render por segundo).
 *
 * Só com `features.site_phases` (desligada por padrão; ligar em produção é PARE, §4.1) e nunca no replay nem na TV.
 */
import { computed, effect, signal } from '@preact/signals';
import { applyTitle, homeTitle } from '../router/effects';
import { canonizeHash } from '../router/navigate';
import { config, isSimulated, now } from '../state/store';
import { hasRouteHash, type SitePhase, sitePhaseAt } from './phaseClock';

// as funções puras moram em phaseClock.ts (o esboço do boot usa as mesmas; ORD-F2-036)
export { hasRouteHash, type SitePhase, sitePhaseAt };

/**
 * Na fase `after`, a apuração completa (o AppShell) está aberta: pelo botão da abertura (com sessão ou sem a porta), por
 * um endereço de rota na entrada (`/presidente` → `/#/presidente`, um link compartilhado) ou porque a aba já mostrava a
 * apuração quando a fase virou (ninguém perde a tela que estava vendo). Sem sessão, a porta decide (app/siteGate.ts).
 */
export const appOpen = signal(false);

/**
 * Abre a apuração completa na fase `after` (sem recarregar). O endereço ganha a rota (`#/presidente`): recarregar dali
 * volta à apuração, e recarregar da abertura (endereço sem rota) volta à abertura.
 */
export function openApp(): void {
  canonizeHash();
  appOpen.value = true;
}

/** Lido em main.tsx antes de o roteador ler o hash; só com acesso ao app (sem sessão, a abertura sempre). */
export function seedAppOpen(hash: string): void {
  if (hasRouteHash(hash)) appOpen.value = true;
}

/**
 * Enquanto a abertura pós-apuração está na tela, o roteador não põe `#/presidente` no endereço sem rota (main.tsx →
 * `holdBareHashWhile`): recarregar a abertura (F5) mostra a abertura de novo, não a apuração.
 */
export function keepBareHash(): boolean {
  return sitePhase.peek() === 'after' && homeShown.peek();
}

/** A fase do site agora; `live` com a chave desligada ou no replay. */
export const sitePhase = computed<SitePhase>(() => {
  const cfg = config.value;
  if (!cfg.features.site_phases || import.meta.env.VITE_SURFACE === 'replay') return 'live';
  const ms = now.value.ms;
  return sitePhaseAt(ms > 0 ? ms : Date.now(), cfg.election_date, cfg.after_at);
});

/**
 * A página inicial está na tela: a fase `home` ou a `after` com a apuração fechada. É o que o B5 (app/feedBinding.ts)
 * usa para buscar devagar e o título da aba para ficar o da página inicial.
 */
export const homeShown = computed(() => {
  const ph = sitePhase.value;
  return ph === 'home' || (ph === 'after' && !appOpen.value);
});

/** O relógio da fonte já está ligado (o contador só confia nele; sem ele, "em instantes"). */
export const clockReady = computed(() => now.value.ms > 0);

/**
 * Título da aba (ORD-F-091): com a página inicial na tela (fase `home`, ou `after` com a apuração fechada), o da página
 * inicial (o mesmo do `<title>` do `index.html`); nas outras, o da rota. É o `title` que o App passa ao
 * `configureRouterEffects`.
 */
export function phaseTitle(base: string): string {
  // a abertura pós-apuração tem o título dela (sem "ao vivo"); a página inicial de antes, o de sempre
  return homeShown.peek() ? homeTitle(isSimulated.peek(), sitePhase.peek() === 'after') : base;
}

/** Regrava o título quando a fase muda sem recarregar (home → pre às 00h00 de 25/10). Devolve o desligamento. */
export function installPhaseTitle(): () => void {
  return effect(() => {
    void sitePhase.value;
    void homeShown.value;
    applyTitle();
  });
}

/**
 * Quem já via a apuração quando a fase virou (`live` → `after`, às 06h00 de 26/10) continua nela: a abertura só aparece
 * para quem chega depois (ou recarrega). Devolve o desligamento.
 */
export function installAfterKeepsApp(): () => void {
  let prev = sitePhase.peek();
  return effect(() => {
    const ph = sitePhase.value;
    if (prev === 'live' && ph === 'after') appOpen.value = true;
    prev = ph;
  });
}
